import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Wallet, Undo2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmt = (n) => new Intl.NumberFormat("tr-TR").format(n);
const METHODS = [{ v: "cash", l: "Nakit" }, { v: "card", l: "Kredi Kartı" }, { v: "iyzico", l: "iyzico (mock)" }];

export default function Payments() {
  const [items, setItems] = useState(null);
  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ member_id: "", package_id: "", amount: "", method: "cash", assign_package: true });
  const [saving, setSaving] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refunding, setRefunding] = useState(false);

  const load = () => api.get("/payments").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/members").then((r) => setMembers(r.data)); api.get("/packages").then((r) => setPackages(r.data)); }, []);

  const onPkg = (pid) => { const p = packages.find((x) => x.id === pid); setForm((f) => ({ ...f, package_id: pid, amount: p ? String(p.price) : f.amount })); };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/payments", { member_id: form.member_id || null, package_id: form.package_id || null, amount: Number(form.amount), method: form.method, assign_package: form.assign_package });
      toast.success("Ödeme kaydedildi" + (form.method === "iyzico" ? " (iyzico MOCK)" : ""));
      setOpen(false); setForm({ member_id: "", package_id: "", amount: "", method: "cash", assign_package: true }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doRefund = async () => {
    setRefunding(true);
    try {
      await api.post(`/payments/${refundTarget.id}/refund`);
      toast.success("Ödeme iade edildi; atanmış paket geri alındı.");
      setRefundTarget(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRefunding(false); }
  };

  return (
    <div data-testid="admin-payments-page">
      <PageHeader title="Ödemeler" subtitle="Paket satışları & tahsilatlar"
        action={<Button onClick={() => setOpen(true)} data-testid="add-payment-button" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold rounded-xl"><Plus size={18} className="mr-1" /> Ödeme Al</Button>} />
      <div className="rounded-xl bg-[#FF5500]/5 border border-[#FF5500]/20 p-3 mb-5 text-xs text-[#A0A0B2]">
        Ödeme sağlayıcı soyutlaması aktif. <b className="text-[#FF5500]">iyzico</b> seçeneği şu an <b>MOCK</b> (gerçek gateway API anahtarlarıyla sonra bağlanacak). Nakit/Kart kayıtları gerçek tahsilat olarak işlenir.
      </div>
      {items === null ? <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl bg-[#13131C]" />)}</div> : (
        <div className="rounded-2xl border border-[#222230] overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="payments-table">
            <thead className="bg-[#181824] text-[#68687B] text-xs uppercase"><tr><th className="px-4 py-3 text-left">Üye</th><th className="px-4 py-3 text-left hidden sm:table-cell">Paket</th><th className="px-4 py-3 text-left">Yöntem</th><th className="px-4 py-3 text-left">Durum</th><th className="px-4 py-3 text-right">Tutar</th><th className="px-4 py-3 text-right">İşlem</th></tr></thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.id} data-testid={`payment-row-${p.id}`} className={`border-t border-[#1A1A24] ${i % 2 ? "bg-[#161622]" : "bg-[#13131C]"}`}>
                  <td className="px-4 py-3 text-white font-medium">{p.member_name}</td>
                  <td className="px-4 py-3 text-[#A0A0B2] hidden sm:table-cell">{p.package_name}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#1B1B27] text-[#A0A0B2] border border-[#2D2D3F]">{p.method}{p.mock ? " · mock" : ""}</span></td>
                  <td className="px-4 py-3">
                    <span data-testid={`payment-status-${p.id}`} className={`text-xs px-2 py-0.5 rounded-full border ${p.status === "paid" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : p.status === "refunded" ? "bg-red-500/10 text-red-400 border-red-500/30" : "bg-amber-500/10 text-amber-400 border-amber-500/30"}`}>
                      {p.status === "paid" ? "Ödendi" : p.status === "refunded" ? "İade Edildi" : "Başarısız"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#FF5500]">₺{fmt(p.amount)}</td>
                  <td className="px-4 py-3 text-right">
                    {p.status === "paid" && (
                      <Button size="sm" variant="ghost" onClick={() => setRefundTarget(p)} data-testid={`refund-payment-button-${p.id}`} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 text-xs">
                        <Undo2 size={14} className="mr-1" /> İade Et
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Wallet size={18} className="text-[#FF5500]" /> Ödeme Al</DialogTitle><DialogDescription className="text-[#68687B]">Paket satışı kaydet; ödeme onaylanınca paket üyeye tanımlanır.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-1">
            <Sel label="Üye" value={form.member_id} onChange={(v) => setForm({ ...form, member_id: v })} testId="payment-member" placeholder="Üye seç" options={members.map((m) => ({ value: m.id, label: m.name }))} />
            <Sel label="Paket" value={form.package_id} onChange={onPkg} testId="payment-package" placeholder="Paket seç" options={packages.map((p) => ({ value: p.id, label: `${p.name} — ₺${fmt(p.price)}` }))} />
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#A0A0B2] mb-1 block">Tutar ₺</label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount" className="bg-[#0E0E14] border-[#262636] text-white" /></div>
              <Sel label="Yöntem" value={form.method} onChange={(v) => setForm({ ...form, method: v })} testId="payment-method" options={METHODS.map((m) => ({ value: m.v, label: m.l }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-[#A0A0B2]">İptal</Button>
            <Button onClick={save} disabled={saving || !form.amount} data-testid="payment-submit" className="bg-[#FF5500] hover:bg-[#FF661A] text-white font-bold">{saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="bg-[#13131C] border-[#222230] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Undo2 size={18} className="text-red-400" /> Ödemeyi İade Et</DialogTitle>
            <DialogDescription className="text-[#68687B]">
              {refundTarget && (<><b className="text-white">{refundTarget.member_name}</b> adlı üyenin <b className="text-[#FF5500]">₺{fmt(refundTarget.amount)}</b> tutarındaki ödemesi iade edilecek. Bu ödeme ile atanmış paket üyeden geri alınır. Bu işlem geri alınamaz.</>)}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRefundTarget(null)} data-testid="refund-cancel-button" className="text-[#A0A0B2]">Vazgeç</Button>
            <Button onClick={doRefund} disabled={refunding} data-testid="refund-confirm-button" className="bg-red-500 hover:bg-red-600 text-white font-bold">{refunding ? <Loader2 className="animate-spin" size={16} /> : "İadeyi Onayla"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Sel({ label, value, onChange, options, placeholder, testId }) {
  return (
    <div><label className="text-xs text-[#A0A0B2] mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="bg-[#0E0E14] border-[#262636] text-white"><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="bg-[#181824] border-[#2D2D3F] text-white max-h-64">{options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
